import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit'

import {
  baseApi,
  connectionLost,
  connectionRestored,
  userActionEnded,
  userActionStarted,
  createHttpClient,
  createSaleTrace,
  errorView,
  createPersistenceController,
  installSaleTrace,
  serverAnswered,
  getClientIdentity,
  sessionExpired,
  trace,
  transportRequest,
  type BrowserStorages,
  type ClientRuntimeExtra,
  type ConnectionEvent,
  type HttpClient,
  type PersistenceController,
} from '@krp/client-runtime'
import { createAqsiTraceSink } from '@krp/resource-aqsi-ui'

import { DISABLED_LIVE_CHANNEL, createAppLiveChannel, type LiveTransport } from '../composition/live'
import { registerListeners } from '../composition/listeners'
import { signedOutElsewhere } from '../composition/session'
import type { SessionChannel } from '../composition/sessionChannel'
import { PERSISTENCE_ENTRIES } from '../composition/persistence'
import { rootReducer, type RootState } from './rootReducer'
import type { AppDispatch } from './types'

export interface AppStoreOptions {
  /** fetch браузера; в тестах — подставной. */
  fetch: typeof fetch
  /** XMLHttpRequest браузера - для прогресса загрузки снимка и потока этапов поиска; не задан (тесты) - те же запросы идут через fetch без прогресса. */
  xhr?: () => XMLHttpRequest
  storages: BrowserStorages
  /** Пространство ключей хранилища: приложение + окружение (D-071). */
  namespace: string
  /** Уйти на адрес Gateway (выход): полная навигация браузера. */
  leaveTo: (url: string) => void
  /** Канал между вкладками браузера: выход в одной — выход во всех. */
  channel: SessionChannel
  /** Redux DevTools: только в разработке — иначе кэш с данными 1С/AQSI виден расширению. */
  devTools?: boolean
  /** Настройки transport для тестов (без пауз между повторами). */
  http?: { retryDelayMs?: number; newCorrelationId?: () => string }
  persistDebounceMs?: number
  /** Живой канал (LIVE): как открыть WebSocket. Не задан — канал не подключается (тесты без него). */
  live?: LiveTransport
  /** Сервер сообщил идентификатор сборки клиента (заголовок любого ответа): сравнение с собственной — в приложении. */
  onServerBuild?: (buildId: string) => void
}

/**
 * Один store нового клиента (G3): один кэш RTK Query, один transport, один
 * реестр хранения. Transport передаётся в baseQuery через `extraArgument` —
 * не глобальной переменной модуля.
 */
export function createAppStore(options: AppStoreOptions) {
  let onUnauthorized: () => void = () => {}
  let onConnection: (event: ConnectionEvent) => void = () => {}
  const http: HttpClient = createHttpClient({
    fetch: options.fetch,
    ...(options.xhr !== undefined ? { xhr: options.xhr } : {}),
    onUnauthorized: () => onUnauthorized(),
    onConnection: (event) => onConnection(event),
    // «Оплатить» и любая запись: окно связи нужно, только пока человек ждёт ответа на своё действие.
    onUserAction: () => {
      dispatch(userActionStarted())
      return (reached) => dispatch(userActionEnded({ reached }))
    },
    // Подробная трассировка продажи (RP-SALE-TRACE) получает хронометраж каждого запроса; в лёгком режиме она его отбрасывает.
    onRequestTimed: (timing) => trace.observeRequest(timing),
    // Устройство и вкладка уходят с каждым запросом: к ним привязан вход в статистику кассира (RP-CA4).
    identityHeaders: () => getClientIdentity().headers(),
    ...(options.onServerBuild !== undefined ? { onServerBuild: options.onServerBuild } : {}),
    ...(options.http?.retryDelayMs !== undefined ? { retryDelayMs: options.http.retryDelayMs } : {}),
    ...(options.http?.newCorrelationId !== undefined ? { newCorrelationId: options.http.newCorrelationId } : {}),
  })
  const persistence: PersistenceController<RootState> = createPersistenceController<RootState>({
    entries: PERSISTENCE_ENTRIES,
    storages: options.storages,
    namespace: options.namespace,
    ...(options.persistDebounceMs !== undefined ? { debounceMs: options.persistDebounceMs } : {}),
    onRejected: (key, reason) => console.error(`Хранилище: запись ${key} отклонена — ${reason}`),
  })
  const listeners = createListenerMiddleware()
  const extra: ClientRuntimeExtra = { http }

  const store = configureStore({
    reducer: rootReducer,
    devTools: options.devTools ?? false,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({ thunk: { extraArgument: extra } })
        .prepend(listeners.middleware)
        .concat(baseApi.middleware, persistence.middleware),
  })

  // Настоящий dispatch обязан совпадать с объявленным AppDispatch (app/types.ts).
  const dispatch: AppDispatch = store.dispatch
  // 401 на любой запрос — общий механизм входа (D-085); запрос не повторяется.
  onUnauthorized = () => dispatch(sessionExpired())
  // Связь с сервером одна на всё приложение (решение владельца 23.10.2026): итог каждого запроса — в срез `connection`,
  // окно «Восстанавливаем соединение» и проверки доступности ведёт оболочка (`ConnectionOverlay`, `ConnectionMonitor`).
  // Каждый ответ сервера запоминает свой момент в срезе (`lastOkAt`): возраст данных на экране продажи (RP-SALE-FAST, К-110) и
  // «Последний ответ» в мини-окне «Связь» (RP-UI-LINK) считаются от него, а не от итога запроса. Ход оплаты читается раз в секунду —
  // его ответы и держат возраст свежим.
  onConnection = (event) => {
    if (!event.ok) {
      dispatch(connectionLost({ error: errorView(event.error), at: Date.now() }))
      return
    }
    dispatch(serverAnswered({ at: Date.now() }))
    if (store.getState().connection.status !== 'online') dispatch(connectionRestored())
    // Связь есть: недоставленная трассировка продаж (очередь sessionStorage) уходит фоном и не чаще раза в 15 с.
    void trace.retryQueued()
  }

  // Трассировка продажи (RP-SALE-TRACE): точки собираются всегда, где у человека есть право, и уходят одним фоновым запросом.
  const disposeTrace = installSaleTrace(
    createSaleTrace({
      sink: createAqsiTraceSink((request) => dispatch(transportRequest(request))),
      storage: options.storages.session,
      namespace: options.namespace,
    }),
  )

  const live = options.live === undefined ? DISABLED_LIVE_CHANNEL : createAppLiveChannel(options.live, dispatch)
  registerListeners(listeners.startListening.withTypes<RootState, AppDispatch, ClientRuntimeExtra>(), {
    persistence,
    http,
    leaveTo: options.leaveTo,
    channel: options.channel,
    live,
  })
  options.channel.onSignOut((userId) => dispatch(signedOutElsewhere({ userId })))
  persistence.hydrateDevice(store.dispatch)

  /**
   * Остановить стор. RTK Query ставит на каждую отписку таймер удаления кэша
   * (`keepUnusedDataFor`, 60 с), поллинг и запросы в полёте живут своими
   * таймерами — они переживают экран. В браузере стор живёт, пока жива
   * вкладка, и это не важно; в тестах стор, переживший свой тест, через минуту
   * дёргал бы уведомление подписчиков через `requestAnimationFrame` уже
   * закрытого jsdom-окна (`Cannot read properties of null (reading
   * '_location')`). Сначала размонтировать экран, затем dispose: снять
   * listeners (их эффекты получают отмену) и сбросить кэш (таймеры удаления
   * снимаются, запросы прерываются).
   */
  const dispose = () => {
    disposeTrace()
    live.stop()
    listeners.clearListeners()
    dispatch(baseApi.util.resetApiState())
  }

  return { store, persistence, http, live, dispose }
}

export type AppStore = ReturnType<typeof createAppStore>['store']
